import { useQuery } from "@tanstack/react-query";
import { fetchAccountSummary, type AccountDelta } from "../api/client";

// At-a-glance account health. Renders at the top of the leaderboard so the
// "is my account net positive?" question is answered without clicking.
// Data source: AccountSnapshots table, populated by GetAccountInfo on every
// decision cycle. May be empty on first deploy until snapshots accumulate.

function fmtUsd(v: number | undefined | null) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPct(v: number | undefined | null) {
  if (v === undefined || v === null || Number.isNaN(v)) return "—";
  return `${(Number(v) * 100).toFixed(2)}%`;
}

function deltaSign(d: AccountDelta | undefined) {
  if (!d?.available || d.delta_abs === undefined) return "";
  if (d.delta_abs > 0) return "+";
  return "";  // negative sign is already part of the number
}

function deltaColor(d: AccountDelta | undefined) {
  if (!d?.available || d.delta_abs === undefined) return "#94a3b8";
  if (d.delta_abs > 0) return "#22c55e";
  if (d.delta_abs < 0) return "#ef4444";
  return "#94a3b8";
}

function DeltaCell({ label, d }: { label: string; d: AccountDelta | undefined }) {
  if (!d?.available) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span style={{ color: "#94a3b8", fontSize: 11 }}>{label}</span>
        <span style={{ color: "#475569", fontSize: 16 }}>—</span>
        <span style={{ color: "#475569", fontSize: 10 }}>not enough history</span>
      </div>
    );
  }
  const color = deltaColor(d);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <span style={{ color: "#94a3b8", fontSize: 11 }}>{label}</span>
      <span style={{ color, fontSize: 18, fontWeight: 600 }}>
        {deltaSign(d)}{fmtUsd(d.delta_abs)}
      </span>
      <span style={{ color, fontSize: 11 }}>
        {deltaSign(d)}{fmtPct(d.delta_pct)}
      </span>
    </div>
  );
}

export function AccountSummaryCard({ mode = "paper" }: { mode?: "paper" | "live" }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["account-summary", mode],
    queryFn: () => fetchAccountSummary(mode),
    refetchInterval: 5 * 60 * 1000,  // refresh every 5 minutes
  });

  if (isLoading) {
    return <div className="card" style={{ color: "#94a3b8" }}>Loading account…</div>;
  }
  if (error) {
    return <div className="card" style={{ color: "#fca5a5" }}>Account summary error: {String(error)}</div>;
  }
  if (!data?.current) {
    return (
      <div className="card" style={{ color: "#94a3b8" }}>
        {data?.message ?? "No account snapshots yet."} Snapshots are written every decision cycle;
        check back after market open.
      </div>
    );
  }

  const c = data.current;
  const allTime = data.deltas.all_time;
  const netPositive = allTime?.available && (allTime.delta_abs ?? 0) >= 0;
  const headerColor = !allTime?.available ? "#94a3b8" : netPositive ? "#22c55e" : "#ef4444";
  // "on_margin" flag is written by GetAccountInfo whenever cash < 0. Fall
  // back to deriving from cash itself for snapshot rows written before the
  // field was added.
  const onMargin = c.on_margin ?? (typeof c.cash === "number" && c.cash < 0);
  const marginDebt = typeof c.cash === "number" && c.cash < 0 ? -c.cash : 0;
  const hasLifetimePL = c.realized_pl !== undefined || c.unrealized_pl !== undefined;

  return (
    <div className="card" style={{ borderLeft: `4px solid ${headerColor}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
        <div>
          <div style={{ color: "#94a3b8", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5 }}>
            Account ({mode}{c.account_type ? ` · ${c.account_type}` : ""})
          </div>
          <div style={{ fontSize: 32, fontWeight: 700 }}>{fmtUsd(c.portfolio_value)}</div>
          <div style={{ color: "#94a3b8", fontSize: 12 }}>
            {/* Decompose Tradier's netted `cash` field into separate "Cash"
                (the positive part) and "Margin" (the absolute value of any
                negative balance) so a negative cash balance doesn't read as
                "you're underwater" when it's really just a margin loan
                against valuable holdings. */}
            Cash {fmtUsd(Math.max(0, c.cash))}
            {onMargin && (
              <>
                {" · "}
                <span style={{ color: "#fbbf24" }}>Margin {fmtUsd(marginDebt)} owed</span>
              </>
            )}
            {" · "}Holdings {fmtUsd(c.holdings_value)} · {c.num_positions} positions
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          {allTime?.available ? (
            <>
              <div style={{ color: headerColor, fontSize: 22, fontWeight: 700 }}>
                {netPositive ? "Net positive" : "Net negative"}
              </div>
              <div style={{ color: headerColor, fontSize: 13 }}>
                {deltaSign(allTime)}{fmtUsd(allTime.delta_abs)} ({deltaSign(allTime)}{fmtPct(allTime.delta_pct)}) since {String(allTime.from_ts ?? "").slice(0, 10)}
              </div>
            </>
          ) : (
            <div style={{ color: "#94a3b8", fontSize: 13 }}>history accumulating…</div>
          )}
        </div>
      </div>

      {onMargin && (
        <div
          role="status"
          style={{
            background: "#451a03",
            border: "1px solid #f59e0b",
            color: "#fbbf24",
            padding: "8px 12px",
            borderRadius: 4,
            fontSize: 12,
            marginBottom: 12,
          }}
        >
          {/* Informational, not alarmist: margin debt is real, but it's
              secured by a much larger holdings value. The point is "the
              coordinator won't open new positions" — not "your account is
              underwater". */}
          <strong>Using margin.</strong>{" "}
          {fmtUsd(marginDebt)} owed to the broker, secured by {fmtUsd(c.holdings_value)} in holdings
          {" "}(net equity {fmtUsd(c.portfolio_value)}). The coordinator is configured to block
          new buys until cash returns above the reserve; sells continue normally and reduce the
          margin balance.
        </div>
      )}

      {hasLifetimePL && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, paddingBottom: 12, borderBottom: "1px solid #1e293b", marginBottom: 12 }}>
          <div>
            <div style={{ color: "#94a3b8", fontSize: 11 }}>Realized P&L (closed trades, lifetime)</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: (c.realized_pl ?? 0) >= 0 ? "#22c55e" : "#ef4444" }}>
              {c.realized_pl !== undefined ? `${(c.realized_pl ?? 0) >= 0 ? "+" : ""}${fmtUsd(c.realized_pl)}` : "—"}
            </div>
          </div>
          <div>
            <div style={{ color: "#94a3b8", fontSize: 11 }}>Unrealized P&L (open positions)</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: (c.unrealized_pl ?? 0) >= 0 ? "#22c55e" : "#ef4444" }}>
              {c.unrealized_pl !== undefined ? `${(c.unrealized_pl ?? 0) >= 0 ? "+" : ""}${fmtUsd(c.unrealized_pl)}` : "—"}
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 16, paddingTop: 12, borderTop: "1px solid #1e293b" }}>
        <DeltaCell label="Last 24h" d={data.deltas.day} />
        <DeltaCell label="Last 7d" d={data.deltas.week} />
        <DeltaCell label="Last 30d" d={data.deltas.month} />
      </div>
      <div style={{ marginTop: 8, fontSize: 10, color: "#475569" }}>
        Snapshot at {String(c.snapshot_ts).slice(0, 19)} UTC · {data.snapshot_count} snapshots recorded
      </div>
    </div>
  );
}
